import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Wallet, CheckCircle, Clock, XCircle, Download } from 'lucide-react';

// Mapping valeurs backend (types de commission FR) -> clés i18n pour l'affichage, repli sur valeur brute.
const TYPE_LABEL_KEYS = {
  ventes: 'staffCommissions.typeVentes',
  commandes: 'staffCommissions.typeCommandes',
  acomptes: 'staffCommissions.typeAcomptes',
  reparations: 'staffCommissions.typeReparations',
  factures: 'staffCommissions.typeFactures',
  devis: 'staffCommissions.typeDevis',
  b2b: 'staffCommissions.typeB2b',
  deplacement: 'staffCommissions.typeDeplacement',
};

export default function StaffCommissionsPage() {
  const { api } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ by_status: {}, by_type: {} });
  const [filter, setFilter] = useState('');
  const today = new Date();
  const [pdfYear, setPdfYear] = useState(today.getFullYear());
  const [pdfMonth, setPdfMonth] = useState(today.getMonth() + 1);
  const [downloading, setDownloading] = useState(false);

  // Libellé de type affiché : clé i18n si connue, sinon valeur backend brute.
  const typeLabel = (ty) => (TYPE_LABEL_KEYS[ty] ? t(TYPE_LABEL_KEYS[ty]) : ty);

  const load = async () => {
    try {
      const r1 = await api.get(`/me/commissions${filter ? `?status=${filter}` : ''}`);
      setItems(r1.data.items || []);
      const r2 = await api.get('/me/commissions/summary');
      setSummary(r2.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, [filter]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/me/commissions/pdf?year=${pdfYear}&month=${pdfMonth}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commissions_${pdfYear}_${String(pdfMonth).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(t('staffCommissions.pdfDownloadError'));
    }
    setDownloading(false);
  };

  const pending = summary.by_status?.pending || 0;
  const paid = summary.by_status?.paid || 0;

  return (
    <Layout>
      <div className="p-6 space-y-6 text-slate-100">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Wallet className="w-8 h-8 text-amber-400" /> {t('staffCommissions.title')}
        </h1>

        <section
          className="bg-slate-900/60 border border-amber-500/40 rounded-xl p-4 flex flex-wrap items-center gap-3"
          data-testid="my-commissions-pdf-section"
        >
          <Download className="w-5 h-5 text-amber-400" />
          <span className="text-slate-200 font-medium">{t('staffCommissions.downloadMonthSheet')}</span>
          <select
            value={pdfMonth}
            onChange={e => setPdfMonth(Number(e.target.value))}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
            data-testid="my-commissions-pdf-month"
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>{t(`staffCommissions.month${i + 1}`)}</option>
            ))}
          </select>
          <select
            value={pdfYear}
            onChange={e => setPdfYear(Number(e.target.value))}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
            data-testid="my-commissions-pdf-year"
          >
            {[today.getFullYear(), today.getFullYear() - 1, today.getFullYear() - 2].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={downloadPdf}
            disabled={downloading}
            className="ml-auto bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold px-4 py-2 rounded text-sm flex items-center gap-2"
            data-testid="my-commissions-pdf-download"
          >
            <Download className="w-4 h-4" />
            {downloading ? t('staffCommissions.generating') : t('staffCommissions.downloadPdf')}
          </button>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl p-6">
            <div className="text-sm uppercase opacity-80">{t('staffCommissions.toReceive')}</div>
            <div className="text-4xl font-bold mt-1">{formatCurrency(pending)}</div>
          </div>
          <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-xl p-6">
            <div className="text-sm uppercase opacity-80">{t('staffCommissions.alreadyReceived')}</div>
            <div className="text-4xl font-bold mt-1">{formatCurrency(paid)}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-6">
            <div className="text-sm uppercase text-slate-400">{t('staffCommissions.totalCumulative')}</div>
            <div className="text-4xl font-bold mt-1">{formatCurrency(pending + paid)}</div>
          </div>
        </div>

        <section className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
          <h2 className="text-lg font-semibold mb-3 text-amber-400">{t('staffCommissions.detailByType')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(summary.by_type || {}).map(([ty, vals]) => (
              <div key={ty} className="bg-slate-800/50 rounded p-3">
                <div className="text-xs text-slate-400 uppercase">{typeLabel(ty)}</div>
                <div className="text-amber-300">{t('staffCommissions.toPay', { amount: formatCurrency(vals.pending || 0) })}</div>
                <div className="text-emerald-300">{t('staffCommissions.paidDetail', { amount: formatCurrency(vals.paid || 0) })}</div>
              </div>
            ))}
            {!Object.keys(summary.by_type || {}).length && (
              <div className="col-span-full text-slate-500 text-center py-4">{t('staffCommissions.noCommissionYet')}</div>
            )}
          </div>
        </section>

        <section>
          <div className="flex gap-2 mb-3">
            {['', 'pending', 'paid', 'cancelled'].map(s => (
              <button
                key={s || 'all'}
                onClick={() => setFilter(s)}
                className={`px-3 py-1 rounded text-sm ${filter === s ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-300'}`}
              >
                {s === '' ? t('staffCommissions.filterAll') : s === 'pending' ? t('staffCommissions.filterPending') : s === 'paid' ? t('staffCommissions.filterPaid') : t('staffCommissions.filterCancelled')}
              </button>
            ))}
          </div>

          <div className="bg-slate-900/60 border border-slate-700 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-400 border-b border-slate-700">
                <tr>
                  <th className="text-left p-3">{t('staffCommissions.colDate')}</th>
                  <th className="text-left">{t('staffCommissions.colType')}</th>
                  <th className="text-left">{t('staffCommissions.colTransaction')}</th>
                  <th className="text-right">{t('staffCommissions.colAmountTtc')}</th>
                  <th className="text-right">{t('staffCommissions.colCommission')}</th>
                  <th className="text-center">{t('staffCommissions.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-b border-slate-800">
                    <td className="p-3">{(it.created_at || '').slice(0, 10)}</td>
                    <td className="capitalize">{typeLabel(it.type)}</td>
                    <td className="font-mono text-xs">{it.transaction_id}</td>
                    <td className="text-right font-mono">{formatCurrency(Number(it.transaction_amount || 0))}</td>
                    <td className="text-right font-mono text-amber-400">{formatCurrency(Number(it.commission_amount))}</td>
                    <td className="text-center">
                      {it.status === 'pending' && <Clock className="inline w-4 h-4 text-amber-400" />}
                      {it.status === 'paid' && <CheckCircle className="inline w-4 h-4 text-emerald-400" />}
                      {it.status === 'cancelled' && <XCircle className="inline w-4 h-4 text-rose-400" />}
                    </td>
                  </tr>
                ))}
                {!items.length && (
                  <tr><td colSpan={6} className="text-center py-4 text-slate-500">{t('staffCommissions.noCommission')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </Layout>
  );
}
